import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, TrendingDown, Minus, Users, ShoppingCart, Loader2 } from 'lucide-react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
  CardFooter,
} from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

interface RevenueImpactAnalysisProps {
  startDate: string;
  endDate: string;
}

const RevenueImpactAnalysis: React.FC<RevenueImpactAnalysisProps> = ({ startDate, endDate }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    // Mock data
    setTimeout(() => {
      setData({
        total_revenue_influenced: 125000,
        conversations_with_revenue: 45,
        avg_revenue_per_conversation: 2778,
        revenue_by_channel: [
          { channel: 'Email', revenue: 45000, conversations: 18 },
          { channel: 'Chat', revenue: 52000, conversations: 20 },
          { channel: 'Phone', revenue: 28000, conversations: 7 },
        ],
        revenue_trend: Array.from({ length: 30 }, (_, i) => ({
          date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          revenue: 3000 + Math.random() * 2000,
        })),
      });
      setLoading(false);
    }, 500);
  }, [startDate, endDate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <Alert className="m-2">
        <AlertDescription>No revenue data available</AlertDescription>
      </Alert>
    );
  }

  const revenueChartOptions: ApexOptions = {
    chart: { type: 'area', toolbar: { show: false } },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 3 },
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.3 } },
    xaxis: { categories: data.revenue_trend.map((d: any) => d.date), labels: { rotate: -45 } },
    yaxis: { labels: { formatter: (val: number) => `$${val.toFixed(0)}` } },
    colors: ['#4caf50'],
    tooltip: { y: { formatter: (val: number) => `$${val.toLocaleString()}` } },
  };

  return (
    <div className="space-y-6">
      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Total Revenue Influenced</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              ${data.total_revenue_influenced.toLocaleString()}
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Through support conversations</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Conversations with Revenue</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <ShoppingCart className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.conversations_with_revenue}
            </CardTitle>
          </CardHeader>
          <CardFooter className="gap-2">
            <Badge variant="outline" className="text-xs font-medium gap-1 border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
              <TrendingUp className="h-3 w-3" />
              +12.0%
            </Badge>
            <span className="text-xs text-muted-foreground">vs previous period</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Avg Revenue / Conversation</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <Users className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              ${data.avg_revenue_per_conversation.toLocaleString()}
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Per resolved conversation</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Revenue Trend</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">15%</CardTitle>
          </CardHeader>
          <CardFooter className="gap-2">
            <Badge variant="outline" className="text-xs font-medium gap-1 border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
              <TrendingUp className="h-3 w-3" />
              +15.0%
            </Badge>
            <span className="text-xs text-muted-foreground">month-over-month</span>
          </CardFooter>
        </Card>
      </div>

      {/* Revenue Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Revenue Trend (30 Days)</CardTitle>
          <CardDescription>Daily revenue influenced through support conversations</CardDescription>
        </CardHeader>
        <CardContent>
          <Chart
            options={revenueChartOptions}
            series={[{ name: 'Revenue', data: data.revenue_trend.map((d: any) => d.revenue) }]}
            type="area"
            height={300}
          />
        </CardContent>
      </Card>

      {/* Revenue by Channel */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Revenue by Channel</CardTitle>
          <CardDescription>Breakdown of revenue attribution across support channels</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            {data.revenue_by_channel.map((channel: any) => (
              <Card key={channel.channel} className="bg-muted/30">
                <CardContent className="pt-4">
                  <p className="text-sm font-medium mb-1">{channel.channel}</p>
                  <p className="text-2xl font-bold">${channel.revenue.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {channel.conversations} conversations
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Attribution Note */}
      <Alert>
        <AlertDescription>
          <span className="font-medium">Revenue Attribution: </span>
          Revenue is attributed when conversations lead to purchases within 30 days. Data tracked via
          conversation tags and CRM integration.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default RevenueImpactAnalysis;
